import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const ERROR_MESSAGES = {
  datos_invalidos: "El enlace no es válido. Vuelve a solicitar uno nuevo.",
  token_invalido: "El enlace no es válido o ya fue invalidado.",
  token_ya_usado: "Este enlace ya se ha usado. Pide uno nuevo desde el login.",
  token_expirado: "El enlace ha caducado. Pide uno nuevo desde el login.",
};

export default function AuthVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verificarLink } = useAuth();
  const [estado, setEstado] = useState("verificando");
  const [error, setError] = useState(null);
  const ejecutado = useRef(false);

  useEffect(() => {
    if (ejecutado.current) return;
    ejecutado.current = true;

    const email = (params.get("email") || "").trim().toLowerCase();
    const token = (params.get("token") || "").trim();

    if (!email || !token) {
      setEstado("error");
      setError(ERROR_MESSAGES.datos_invalidos);
      return;
    }

    (async () => {
      try {
        const data = await verificarLink(email, token);
        const destino = data?.onboarding?.completado ? "/" : "/onboarding";
        setEstado("ok");
        navigate(destino, { replace: true });
      } catch (err) {
        console.error("verify_link_error", err);
        const code = err?.response?.data?.error;
        setError(ERROR_MESSAGES[code] || "No se pudo iniciar sesión");
        setEstado("error");
      }
    })();
  }, [params, verificarLink, navigate]);

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="rx-card p-8 text-center">
          {estado === "verificando" ? (
            <>
              <div className="inline-flex w-12 h-12 rounded-full bg-blue-50 text-blue-600 items-center justify-center mb-4">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
              <h1 className="font-display font-bold text-xl tracking-tight mb-1">
                Verificando enlace…
              </h1>
              <p className="text-sm text-slate-500">
                Iniciando sesión en tu cuenta.
              </p>
            </>
          ) : estado === "ok" ? (
            <>
              <div className="inline-flex w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h1 className="font-display font-bold text-xl tracking-tight mb-1">
                Sesión iniciada
              </h1>
              <p className="text-sm text-slate-500">Redirigiendo…</p>
            </>
          ) : (
            <div data-testid="auth-verify-error">
              <div className="inline-flex w-12 h-12 rounded-full bg-red-50 text-red-600 items-center justify-center mb-4">
                <ShieldAlert className="w-6 h-6" />
              </div>
              <h1 className="font-display font-bold text-xl tracking-tight mb-1">
                Enlace no válido
              </h1>
              <p className="text-sm text-slate-600 mb-5">{error}</p>
              <Link to="/login">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Volver al login
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
